import React, { lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";

// Auth0
import Auth0ProviderWithNavigate from "@/components/auth/Auth0ProviderWithNavigate";
import ProtectedRoute from "@/components/route/ProtectedRoute";
import CallbackPage from "@/pages/auth/CallbackPage";

// Pages
import Crypto from "@/pages/Crypto";
import Blank from "@/pages/others/Blank";
import ProjectCardPage from "@/pages/pre-built/projects/ProjectCard";
import InvoicePrint from "@/pages/pre-built/invoice/InvoicePrint";
import Error404Classic from "@/pages/error/404-classic";
import Error404Modern from "@/pages/error/404-modern";
import Error504Modern from "@/pages/error/504-modern";
import Error504Classic from "@/pages/error/504-classic";
import Layout from "@/layout/Index";
import LayoutNoSidebar from "@/layout/Index-nosidebar";
import ThemeProvider from "@/layout/provider/Theme";
import ApiDocsPage from "../pages/ApiDocsPage";
import { apiUrl } from '@/utils/apiUrl'
import TradingOverview from '@/pages/TradingOverview'

// Load specialist views on demand, especially the 3D operations map and charts.
const SignalDashboard = lazy(() => import('../pages/SignalDashboard'));
const MarketData = lazy(() => import('../pages/MarketData'));
const SignalHistory = lazy(() => import('../pages/SignalHistory'));
const BotStatusDashboard = lazy(() => import('../pages/BotStatusCard'));
const DailyPnlDashboard = lazy(() => import('../pages/DailyPnlDashboard'));
const OrdersDashboard = lazy(() => import('../pages/OrdersDashboard'));
const CryptoExchangeInstruments = lazy(() => import('../pages/CryptoExchangeInstruments'));
const TradeStatsDashboard = lazy(() => import('../pages/TradeStatsDashboard'));
const HeartbeatNetworkPage = lazy(() => import('../pages/HeartbeatNetworkPage'));
const CircuitBreakerDashboard = lazy(() => import('../pages/CircuitBreakerDashboard'));
const SpreadSummaryPage = lazy(() => import('../pages/SpreadSummaryPage'));

function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.assign(to);
  }, [to]);

  return null;
}

const ScrollToTop = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return <>{children}</>;
};

const Pages = () => {
  return (
    <Auth0ProviderWithNavigate>
      <ScrollToTop>
        <Routes>
          <Route element={<ThemeProvider />}>
            <Route element={<Layout />}>
              {/* Public routes */}
              <Route
                index
                element={
                  <ProtectedRoute>
                    <TradingOverview />
                  </ProtectedRoute>
                }
              />

              <Route path="crypto" element={<Crypto />} />
              <Route path="_blank" element={<Blank />} />
              <Route path="project-card" element={<ProjectCardPage />} />

              {/* Swagger aliases */}
              <Route
                path="/docs/*"
                element={
                  <ProtectedRoute>
                    <ApiDocsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="api/openapi.json"
                element={
                  <ProtectedRoute>
                    <ExternalRedirect to={apiUrl("/api/openapi.json")} />
                  </ProtectedRoute>
                }
              />

              {/* Secured routes */}
              <Route
                path="signals-dashboard"
                element={
                  <ProtectedRoute>
                    <SignalDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="order-history/:broker/:instrument"
                element={
                  <ProtectedRoute>
                    <OrdersDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="bots-dashboard"
                element={
                  <ProtectedRoute>
                    <BotStatusDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="heartbeat-network"
                element={
                  <ProtectedRoute>
                    <HeartbeatNetworkPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="circuit-breakers"
                element={
                  <ProtectedRoute>
                    <CircuitBreakerDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="signal-history/:broker/:instrument"
                element={
                  <ProtectedRoute>
                    <SignalHistory />
                  </ProtectedRoute>
                }
              />

              <Route
                path="market-data"
                element={
                  <ProtectedRoute>
                    <MarketData />
                  </ProtectedRoute>
                }
              />

              <Route
                path="daily-pnl"
                element={
                  <ProtectedRoute>
                    <DailyPnlDashboard />
                  </ProtectedRoute>
                }
              />

              <Route 
                path='trade-stats' 
                element={<ProtectedRoute><TradeStatsDashboard /></ProtectedRoute>}
              />

              <Route
                path="instruments-crypto-exchanges"
                element={
                  <ProtectedRoute>
                    <CryptoExchangeInstruments />
                  </ProtectedRoute>
                }
              />

              <Route
                path="spread-summary"
                element={
                  <ProtectedRoute><SpreadSummaryPage /></ProtectedRoute>
                }
              />
            </Route>

            {/* No-sidebar, errors and prints */}
            <Route element={<LayoutNoSidebar />}>
              <Route path="errors/404-modern" element={<Error404Modern />} />
              <Route path="errors/404-classic" element={<Error404Classic />} />
              <Route path="errors/504-modern" element={<Error504Modern />} />
              <Route path="errors/504-classic" element={<Error504Classic />} />
              <Route path="invoice-print/:invoiceId" element={<InvoicePrint />} />
              <Route path="*" element={<Error404Modern />} />
            </Route>

            {/* Auth0 callback */}
            <Route path="/callback" element={<CallbackPage />} />
          </Route>
        </Routes>
      </ScrollToTop>
    </Auth0ProviderWithNavigate>
  );
};

export default Pages;
